<template>
    <cr-app id="app" class="full-page">
        <guacamole-header :challenge-environment-id="props.challengeEnvironmentId" :sessionExpirationTimestamp="challengeEnvironmentLaunchResponse.sessionExpirationTimestamp" :showClockOutOfSyncMessage="showClockOutOfSyncMessage" @session-ended="onSessionEnded"/>
        <guacamole-iframe v-if="challengeEnvironmentLaunchResponse.guacamoleAccessPointId" :guacamoleAccessPointId="challengeEnvironmentLaunchResponse.guacamoleAccessPointId" :disabled="sessionDisabled"/>
        <main v-else class="connection-message" data-testing="cyber-range-connecting-message">
            <span v-if="errorMessage">{{ errorMessage }}</span>
            <cr-loading v-else :message="connectingMessage" color="#989898"/>
        </main>
        <error-dialog />
    </cr-app>
</template>

<script setup lang="ts">
import { ChallengeEnvironmentLaunchResponse, ChallengeEnvironmentLaunchStatus, IChallengeEnvironmentLaunchResponse } from "@cyber-range/cyber-range-api-ctf-challenge-client";
import { computed, ref, watch } from "vue";
import { useChallengeEnvironmentStore } from "../../stores/challengeEnvironmentStore";
import GuacamoleHeader from "./GuacamoleHeader.vue";
import GuacamoleIframe from "./GuacamoleIframe.vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    challengeEnvironmentId: string
}>()
const challengeEnvironmentLaunchResponse = ref(new ChallengeEnvironmentLaunchResponse());

const { t } = useI18n()

const errorMessage = ref('');
const connectionErrorCount = ref(0);
const connectingMessage = computed(() => 
{
    const messageKey = connectionErrorCount.value === 0
        ? 'CYBER_RANGE_CONNECTION_MESSAGE'
        : connectionErrorCount.value === 1
            ? 'CYBER_RANGE_CONNECTION_HAD_ERROR_MESSAGE'
            : 'CYBER_RANGE_CONNECTION_FAILED_MESSAGE';
    let status = challengeEnvironmentLaunchResponse.value.status?.replace('pending_', '').toLowerCase();
    if (status === 'error' || status === 'stopping')
    {
        status = 'restarting';
    }
    status ||= 'loading'
    return t(messageKey, { status })
});

const sessionDisabled = ref(false);
const onSessionEnded = () => sessionDisabled.value = true;

const showClockOutOfSyncMessage = ref(false);

let launchInterval: NodeJS.Timeout;
async function launchEnvironment()
{
    let response: IChallengeEnvironmentLaunchResponse
    try
    {
        response = await useChallengeEnvironmentStore().launch(props.challengeEnvironmentId);
    }
    catch (e)
    {
        errorMessage.value = e;
        clearInterval(launchInterval);
        return;
    }
    const { currentTimestamp: systemTimestamp } = response;

    if (systemTimestamp)
    {
        const ourTime = new Date().getTime();
        const systemTime = new Date(systemTimestamp).getTime();
        const timeDelta = Math.abs(ourTime - systemTime)
        showClockOutOfSyncMessage.value = timeDelta > 60000;
    }

    challengeEnvironmentLaunchResponse.value = response;
}
watch(() => challengeEnvironmentLaunchResponse.value.status, async (newStatus, oldStatus) =>
{
    if (newStatus && newStatus === oldStatus) return;

    switch (newStatus) {
        case ChallengeEnvironmentLaunchStatus.READY:
            clearInterval(launchInterval);
            connectionErrorCount.value = 0;
            break;
        case ChallengeEnvironmentLaunchStatus.ERROR:
            connectionErrorCount.value++
            if (connectionErrorCount.value > 1)
            {
                errorMessage.value = t('CYBER_RANGE_CONNECTION_FAILED_MESSAGE').toString();
                clearInterval(launchInterval);
                break;
            }
            await useChallengeEnvironmentStore().reset(props.challengeEnvironmentId);
            break;
        default:
            if (launchInterval === undefined)
            {
                launchInterval = setInterval(launchEnvironment, 5000);
                await launchEnvironment()
            }
            break;
    }
}, { immediate: true });

window.onbeforeunload = (ev: BeforeUnloadEvent) => 
{
    ev.preventDefault();
    useChallengeEnvironmentStore().stop(props.challengeEnvironmentId)
    return true;
}
</script>

<style scoped>
#app {
    background: black;
}
.full-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.connection-message {
    margin: auto;
    text-align: center;
    color: #989898;
    font-family: monospace;
    font-size: 1rem;
}
</style>
